import type { PropsWithChildren, ReactNode } from 'react'
import {
	AlertFillCircleIcon,
	CheckCircleFillIcon,
	InfoFillCircleIcon
} from '@/components/ui/icons'
import { Tooltip } from '@/components/ui/tooltip'
import { useTranslation } from '@/translations/i18n'

export interface GateItem {
	/** Stable identity of the slot, since the title may be a node. */
	id: string
	/**
	 * `ok` passes, `warning` blocks, `info` is a passed check stated for the
	 * record (e.g. rows marked unresolvable: excluded, not blocking).
	 */
	tone: 'ok' | 'warning' | 'info'
	/** Plain copy, or copy with an inline affordance (see `GateListTooltip`). */
	title: ReactNode
	detail: string
}

/**
 * Underlined span inside a gate title that lists `entries` in a tooltip on
 * hover, the same bulleted tooltip the preview grid uses for a row's CTAs. Used
 * as a `Trans` component so the copy decides which words carry the list.
 *
 * A single entry is plain text: a bullet in front of one item is a list of
 * one, which reads as a list that got cut short.
 */
export function GateListTooltip({
	entries,
	children
}: PropsWithChildren<{ entries: string[] }>) {
	return (
		<Tooltip
			content={
				entries.length === 1 ? (
					<span className='font-normal'>{entries[0]}</span>
				) : (
					<ul className='ml-4 list-disc font-normal'>
						{entries.map((entry) => (
							<li key={entry}>{entry}</li>
						))}
					</ul>
				)
			}
		>
			<span className='cursor-pointer underline underline-offset-2'>
				{children}
			</span>
		</Tooltip>
	)
}

interface ExportValidationGateProps {
	items: GateItem[]
}

// The icons paint via `fill='currentColor'`, so the color must come from
// `text-*`; a `fill-*` utility leaves them black.
const gateIcon = {
	ok: (
		<CheckCircleFillIcon className='mt-0.5 size-5 shrink-0 text-success-600' />
	),
	warning: (
		<AlertFillCircleIcon className='mt-0.5 size-5 shrink-0 text-orange-600' />
	),
	info: (
		<InfoFillCircleIcon className='mt-0.5 size-5 shrink-0 text-neutral-400' />
	)
}

export function ExportValidationGate({ items }: ExportValidationGateProps) {
	const { t } = useTranslation()
	// An info item is a passed check with a caveat, so it does not hold the
	// "all checks passed" line back; only a warning does.
	const allPassed =
		items.length > 0 && items.every((item) => item.tone !== 'warning')

	return (
		<section className='flex flex-col gap-6 rounded-airbnb-2xl bg-grey-50 l:p-8'>
			<div className='flex flex-col gap-1'>
				<h3 className='font-semibold text-lg text-neutral-900'>
					{t('marquee:export_gate_title')}
				</h3>
				<p className='text-neutral-500 text-sm'>
					{allPassed
						? t('marquee:export_gate_all_passed')
						: t('marquee:export_gate_subtitle')}
				</p>
			</div>

			<ul className='flex flex-col gap-4'>
				{items.map((item) => (
					<li className='flex items-start gap-3' key={item.id}>
						{gateIcon[item.tone]}
						<div className='flex flex-col'>
							<span className='font-medium text-neutral-900 text-sm'>
								{item.title}
							</span>
							<span className='text-neutral-500 text-sm'>
								{item.detail}
							</span>
						</div>
					</li>
				))}
			</ul>
		</section>
	)
}
